import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const Kitsilano = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.kitsilano-vancouver:About Kitsilano Paragraph">
          Kitsilano, affectionately known as &quot;Kits,&quot; is a vibrant
          neighborhood located on Vancouver’s west side in{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia
          </LinkText>
          . Renowned for its stunning beaches, particularly Kitsilano Beach, the
          area offers breathtaking views of English Bay and the North Shore
          Mountains. Kitsilano seamlessly blends a laid-back, beach-town
          atmosphere with urban sophistication, featuring tree-lined streets,
          heritage homes, and a plethora of boutique shops, cafes, and
          restaurants. The neighborhood’s active lifestyle is evident with
          numerous parks, yoga studios, and outdoor recreational opportunities,
          making it a sought-after destination for both residents and visitors.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$2,300 - $2,999',
          bed2: '$2,790 - $3,850',
          bed3: '$3,800 - $5,500'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.kitsilano-vancouver:Apartment Hunting Kitsilano Paragraph 1">
            Finding an apartment for rent in Kitsilano, Vancouver, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Kitsilano lifestyle by exploring your
            new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.kitsilano-vancouver:Apartment Hunting Kitsilano Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kitsilano-vancouver:Apartments for rent in Kitsilano Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Kitsilano, Vancouver
              </LinkText>
              , cater to young professionals and students seeking a vibrant
              community. These units range from cozy studios to spacious
              multi-bedroom layouts, often in well-maintained low-rise buildings
              or modern condominiums. Renting in Kitsilano offers proximity to
              beaches, parks, and a lively commercial district, providing a
              balanced urban lifestyle.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kitsilano-vancouver:Houses for rent in Kitsilano Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Kitsilano, Vancouver
              </LinkText>
              , are ideal for families and individuals desiring more space and
              privacy. These properties often feature charming heritage designs
              with multiple bedrooms and private yards. Renting a house in
              Kitsilano provides a suburban feel while maintaining easy access
              to urban amenities, schools, and recreational facilities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kitsilano-vancouver:Townhouses for rent in Kitsilano Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Kitsilano, Vancouver
              </LinkText>{' '}
              , appeal to those seeking a blend of apartment convenience and
              house-like space. These multi-level homes typically offer modern
              designs with two or more bedrooms, private entrances, and
              sometimes small gardens. Renting a townhouse in Kitsilano ensures
              close proximity to local shops, cafes, and the scenic waterfront.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kitsilano-vancouver:Basements for rent in Kitsilano Paragraph">
              Basements for rent in Kitsilano, Vancouver, provide affordable
              living options for students and singles. These suites, located
              within houses, offer separate entrances and essential amenities.
              Renting a basement in Kitsilano allows residents to enjoy the
              neighborhood’s vibrant lifestyle on a budget, with easy access to
              public transit and local attractions.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kitsilano-vancouver:Condos for rent Kitsilano Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Kitsilano, Vancouver
              </LinkText>
              , are perfect for individuals and couples seeking modern living
              spaces with amenities like gyms and communal areas. These units
              often feature contemporary designs, balconies, and open-concept
              layouts. Renting a condo in Kitsilano offers a low-maintenance
              lifestyle close to beaches, eateries, and shopping districts.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kitsilano-vancouver:Rooms for rent in Kitsilano Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Kitsilano, Vancouver
              </LinkText>{' '}
              , suit students and young professionals looking for shared
              accommodations. These arrangements typically involve renting a
              bedroom within a shared house or apartment, providing an
              affordable entry into this desirable neighborhood. Living in
              Kitsilano offers a vibrant community experience with access to
              numerous amenities.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kitsilano-vancouver:Short term rentals in Kitsilano Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Kitsilano, Vancouver
              </LinkText>
              , are ideal for tourists, business travelers, or those seeking
              temporary accommodation. Options range from furnished apartments
              to entire homes, offering flexibility and comfort. Staying in
              Kitsilano on a short-term basis allows guests to experience the
              neighborhood’s beaches, dining, and cultural attractions
              firsthand.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.kitsilano-vancouver:Exploring Kitsilano Paragraph">
          Kitsilano, nestled on Vancouver’s west side, is a neighborhood that
          harmoniously combines natural beauty with urban charm. Known for its
          relaxed, beach-town vibe, Kitsilano boasts the famous{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/kitsilano-beach.aspx">
            Kitsilano Beach
          </LinkText>
          , offering panoramic views of{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/english-bay-beach.aspx">
            English Bay
          </LinkText>{' '}
          and the{' '}
          <LinkText href="https://www.vancouvertrails.com/regions/the-north-shore/">
            North Shore Mountains
          </LinkText>
          . The area is dotted with boutique shops, organic markets, and a
          diverse array of eateries along West 4th Avenue and West Broadway.
          Cultural enthusiasts can explore the{' '}
          <LinkText href="https://museumofvancouver.ca/">
            Museum of Vancouver
          </LinkText>
          , located in Vanier Park. The neighborhood’s active lifestyle is
          supported by numerous parks, yoga studios, and cycling routes, making
          Kitsilano a vibrant and dynamic community.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.kitsilano-vancouver:Things To Do Kitsilano Paragraph">
          Kitsilano offers a plethora of activities catering to diverse
          interests. Nature lovers can bask in the sun at{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/kitsilano-beach.aspx">
            Kitsilano Beach
          </LinkText>
          , swim in the outdoor saltwater pool, or enjoy a leisurely stroll
          through{' '}
          <LinkText href="https://www.tripadvisor.ca/Attraction_Review-g154943-d184669-Reviews-Vanier_Park-Vancouver_British_Columbia.html">
            Vanier Park
          </LinkText>
          , which also houses the{' '}
          <LinkText href="https://museumofvancouver.ca/">
            Museum of Vancouver
          </LinkText>{' '}
          and the{' '}
          <LinkText href="https://www.spacecentre.ca/visit/">
            H.R. MacMillan Space Centre
          </LinkText>
          . Art enthusiasts can visit local galleries or attend performances at
          the{' '}
          <LinkText href="https://kitsilanoshowboat.com/">
            Kitsilano Showboat
          </LinkText>{' '}
          during summer evenings. Shoppers will delight in the unique boutiques
          along West 4th Avenue, offering everything from fashion to locally
          made crafts. Foodies can indulge in a variety of cuisines, from casual
          beachside cafes to upscale dining experiences. The neighborhood’s
          vibrant atmosphere ensures that both locals and visitors have a wealth
          of options to explore and enjoy.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.kitsilano-vancouver:Restaurants Kitsilano Paragraph">
        <Paragraph>
          Kitsilano Vancouver offers a range of dining options for every taste.
          Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1) <LinkText href="https://maenam.ca/">Maenam</LinkText> - Maenam is a
          celebrated Thai restaurant known for its contemporary take on
          traditional dishes. Chef Angus An crafts a menu that balances bold
          flavors with local ingredients, offering signature dishes like the
          green papaya salad and 8-spice lingcod. The ambiance is modern yet
          cozy, making it perfect for both casual dinners and special occasions.
          Maenam caters to those seeking an authentic Thai culinary experience
          with a modern twist.
        </Paragraph>
        <Paragraph>
          2) <LinkText href="https://fablekitchen.ca/">Fable Kitchen</LinkText>{' '}
          - Fable Kitchen offers a farm-to-table dining experience with an
          emphasis on fresh, locally sourced ingredients. The menu features
          creative Canadian dishes such as the signature Fable Burger and Duck
          Risotto. The restaurant’s rustic yet modern decor creates a welcoming
          ambiance, making it an excellent choice for brunch or a cozy dinner.
        </Paragraph>
        <Paragraph>
          3) <LinkText href="https://www.annalena.ca/">AnnaLena</LinkText> -
          AnnaLena is a modern Canadian restaurant that blends innovative
          flavors with artistic presentation. Known for its ever-evolving
          tasting menu and expertly crafted cocktails, AnnaLena delivers a
          sophisticated dining experience. The intimate and stylish setting
          makes it perfect for date nights and special occasions.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.kitsilano-vancouver:Transportation Kitsilano Paragraph">
          Getting around Kitsilano, Vancouver, BC, is convenient with various
          transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of Kitsilano are also walkable, with key amenities and
          attractions nearby. Drivers will find a mix of street parking and
          parking garages available. No matter your preferred way of getting
          around, Kitsilano offers accessible and efficient transportation
          choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.kitsilano-vancouver:Where To Stay Kitsilano Paragraph">
          Accommodation options in Kitsilano, Vancouver cater to those looking
          for a more local and immersive experience. This area offers a mix of
          boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, providing
          travelers with a unique stay. While large hotels may be limited within
          Kitsilano itself, its proximity to other parts of Vancouver ensures
          that a range of traditional accommodations, from luxury to
          budget-friendly, is just a short distance away. Staying in Kitsilano
          allows visitors to experience its distinct character while remaining
          conveniently close to the city’s main attractions
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default Kitsilano
